import {
  getString,
  getInt64,
  getObject,
  getOptionalString,
  getUrl,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { AssetType } from "./assetType";
/* eslint-disable import/export */
export abstract class GetPresignedUploadURLRPC implements
  LeoRPC<GetPresignedUploadURLRPC.Request, GetPresignedUploadURLRPC.Response, GetPresignedUploadURLRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedUploadURLRPC.Request
  ): Promise<LeoRPCResult<GetPresignedUploadURLRPC.Response, GetPresignedUploadURLRPC.Errors.Errors>>;
}

export namespace GetPresignedUploadURLRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    filename: string;
    fileSize: number;
    assetType: AssetType.AssetTypeDTO;
    payload?: string | null;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedUploadURLRPCRequest: boolean = true;
  
    readonly filename: string;
    readonly fileSize: number;
    readonly assetType: AssetType.AssetType;
    readonly payload: string | null;
  
    static readonly constraints = {
      "filename": {
        "minSize": 2,
        "maxSize": 500,
        
        
        
        
      },
    }
  
    constructor(
    filename: string, 
    fileSize: number, 
    assetType: AssetType.AssetType, 
    payload: string | null = null
    ) {
      Request.validateFilename(filename);
      this.filename = filename;
      this.fileSize = fileSize;
      this.assetType = assetType;
      if ( payload !== undefined && payload !== null) {
        Request.validatePayload(payload);
      }
      this.payload = payload;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const filename = getString(dto, "filename", InvalidRequestFilenameError);
      const fileSize = getInt64(dto, "fileSize", InvalidRequestFileSizeError);
      const assetType = AssetType.fromDTO(getObject(dto, "assetType", InvalidRequestAssetTypeError));
      const payload = getOptionalString(dto, "payload", InvalidRequestPayloadError);
      return new Request(
        filename,
        fileSize,
        assetType,
        payload
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          filename: this.filename,
          fileSize: this.fileSize,
          assetType: AssetType.toDTO(this.assetType),
      };
      if (this.payload !== undefined && this.payload !== null){
        dto.payload = this.payload;
      }
      return dto;
    }
    private static validateFilename = (filename: string): void => {
      if(filename.length < 2) {
        throw new InvalidRequestFilenameMinSizeError(
          2,
          `Attribute filename is too short. Size is ${filename.length}. Min size is 2.`
        );
      }
      if(filename.length > 500) {
        throw new InvalidRequestFilenameMaxSizeError(
          500,
          `Attribute filename is too long. Size is ${filename.length}. Max size is 500.`
        );
      }
        if(filename.trim().length === 0) {
          throw new InvalidRequestFilenameNonEmptyError(
            `Attribute filename cannot be an empty string.`
          );
        }
    }
    private static validatePayload = (payload: string): void => {
      if(payload.trim().length === 0) {
        throw new InvalidRequestPayloadNonEmptyError(
          `Attribute payload cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedUploadURLRPC(filename='${this.filename}',fileSize='${this.fileSize}',assetType='${this.assetType}',payload='${this.payload}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    presignedUploadURL: string;
    fileId: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedUploadURLResponse: boolean = true;
  
    readonly presignedUploadURL: URL;
    readonly fileId: LeoUUID;
  
  
    constructor(
    presignedUploadURL: URL, 
    fileId: LeoUUID
    ) {
      this.presignedUploadURL = presignedUploadURL;
      this.fileId = fileId;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const presignedUploadURL = getUrl(dto, "presignedUploadURL", InvalidResponsePresignedUploadUrlError);
      const fileId = getLeoUuid(dto, "fileId", InvalidResponseFileIdError);
      return new Response(
        presignedUploadURL,
        fileId
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          presignedUploadURL: this.presignedUploadURL.toString(),
          fileId: this.fileId.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedUploadURL(presignedUploadURL='${this.presignedUploadURL}',fileId='${this.fileId}')`;
  };
  

  export enum RPCError {
    InvalidFileType = "INVALID_FILE_TYPE",
    InvalidFileSize = "INVALID_FILE_SIZE"
  }

  export namespace Errors {
    
    export interface InvalidFileTypeDTO {
      code: string;
    }
    
    export class InvalidFileType extends LeoRPCError<RPCError> {
      readonly isInvalidFileType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidFileType;
        super(code);
      }
    
    
      toDTO(): InvalidFileTypeDTO {
        const dto: InvalidFileTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidFileType.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLRPC.INVALID_FILE_TYPE()`;
    };
    
    
    export interface InvalidFileSizeDTO {
      code: string;
    }
    
    export class InvalidFileSize extends LeoRPCError<RPCError> {
      readonly isInvalidFileSize: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidFileSize;
        super(code);
      }
    
    
      toDTO(): InvalidFileSizeDTO {
        const dto: InvalidFileSizeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidFileSize.prototype.toString = function toString(): string {
      return `GetPresignedUploadURLRPC.INVALID_FILE_SIZE()`;
    };
    

    export type Errors = InvalidFileType | InvalidFileSize;
    export type ErrorsDTO = InvalidFileTypeDTO | InvalidFileSizeDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFileType:
          return new InvalidFileType();
        case RPCError.InvalidFileSize:
          return new InvalidFileSize();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestFilenameError extends GetPresignedUploadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilenameError";
  }
}

export class InvalidRequestFilenameMaxSizeError extends InvalidRequestFilenameError {
  readonly maxSize: number;
  constructor(maxSize: number, message?: string) {
    super(message);
    this.maxSize = maxSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilenameMaxSizeError";
  }
}

export class InvalidRequestFilenameMinSizeError extends InvalidRequestFilenameError {
  readonly minSize: number;
  constructor(minSize: number, message?: string) {
    super(message);
    this.minSize = minSize;
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilenameMinSizeError";
  }
}

export class InvalidRequestFilenameNonEmptyError extends InvalidRequestFilenameError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFilenameNonEmptyError";
  }
}

export class InvalidRequestFileSizeError extends GetPresignedUploadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestFileSizeError";
  }
}

export class InvalidRequestAssetTypeError extends GetPresignedUploadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAssetTypeError";
  }
}

export class InvalidRequestPayloadError extends GetPresignedUploadURLRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPayloadError";
  }
}

export class InvalidRequestPayloadNonEmptyError extends InvalidRequestPayloadError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestPayloadNonEmptyError";
  }
}

export class InvalidResponsePresignedUploadUrlError extends GetPresignedUploadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponsePresignedUploadUrlError";
  }
}

export class InvalidResponseFileIdError extends GetPresignedUploadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseFileIdError";
  }
}

export class InvalidResponseInvalidFileTypeError extends GetPresignedUploadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileTypeError";
  }
}

export class InvalidResponseInvalidFileSizeError extends GetPresignedUploadURLRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileSizeError";
  }
}

